import {
  Alert,
  Button,
  Checkbox,
  Combobox,
  DatePicker,
  Text,
  TextField,
  fromUtcDateValue,
  toUtcDateValue,
} from '@cinesend/ui-kit'
import useUtilities from '../../hooks/use_utilities'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import InputLocalized from './components/input_localized'
import useAuth from '../../hooks/use_auth'
import useStore from '../../hooks/use_store'
import { modelTypes } from '../../constants/model_types'
import { DISABLED_REASONS } from '../../constants/disabled_reasons'
import DisabledTooltip from '../../components/disabled_tooltip'
import SettingsCard from '../../components/settings_card'

const nounForCategory = (category) => {
  if (category.type === 'categories') return 'section'
  if (category.type === 'ad-pools') return 'ad set'
  if (category.type === 'warning-slates') return 'slate set'
  return 'category'
}

const listPathForCategory = (category) => {
  const root = `/airlines/${category.airline_id}/cycles/${category.cycle_id}`
  if (category.type === 'categories') return `${root}/sections`
  if (category.type === 'ad-pools') return `${root}/ads/ad-sets`
  if (category.type === 'warning-slates') return `${root}/ads/slate-sets`
  return `${root}/categories`
}

const Details = ({ category, update, airline }) => {
  const [currentCategory, setCurrentCategory] = useState(category)
  const [externalUniqueNameError, setExternalUniqueNameError] = useState(null)
  const { assetTypes } = useUtilities()
  const pluralAssetTypes = assetTypes?.map((opt) => ({ ...opt, label: opt.plural_label }))
  const { user, canEditModel: canEditCategory } = useAuth(modelTypes.Category, category)
  const { deleteModel } = useStore()
  const navigate = useNavigate()
  const noun = nounForCategory(category)

  const disabled = !canEditCategory
  // Admins can always edit; CSP editors only while no snapshot has been generated since the category was created.
  // Also requires the user to have general edit access to the category (e.g. CSPs cannot edit Sections at all).
  const canEditExternalName = canEditCategory && (user?.has_unrestricted_edit_role || !!category.external_name_editable)
  const editReason = disabled ? DISABLED_REASONS.noPermission : null
  const externalNameReason = !canEditExternalName
    ? !canEditCategory
      ? DISABLED_REASONS.noPermission
      : DISABLED_REASONS.externalNameLocked
    : null

  const isAdRelated = ['ad-pools', 'warning-slates'].includes(category.type)

  const adAllowedAssetTypes = ['movie', 'series']

  const assetTypesOptionsFilter = (value) => {
    if (isAdRelated) {
      return adAllowedAssetTypes.includes(value) && (airline?.ads_asset_types?.includes(value) ?? true)
    } else {
      return airline?.asset_types?.includes(value)
    }
  }

  const deleteCategory = () => {
    const name =
      category.category_values?.find((v) => v.language_id === airline?.languages?.[0]?.id)?.value ||
      category.external_unique_name
    deleteModel.mutate(
      {
        endpoint: 'categories',
        id: category.id,
        deleteNoun: noun,
        deleteName: name,
      },
      {
        onSuccess: () => navigate(listPathForCategory(category)),
      }
    )
  }

  return (
    <div className='flex w-full flex-col gap-12'>
      <SettingsCard title='Details'>
        {category.issues.length > 0 ? (
          <Alert tone='error' size='sm'>
            {category.issues[0]?.description}
          </Alert>
        ) : null}
        <div className='flex items-center space-x-4'>
          {(isAdRelated ? airline?.languages?.slice(0, 1) : airline?.languages)?.map((lang) => {
            return (
              <DisabledTooltip key={`title-${lang.id}`} reason={editReason} className='block w-full'>
                <InputLocalized
                  categoryID={category.id}
                  langID={lang.id}
                  label={isAdRelated ? 'Title' : `${lang?.eng_description} Title`}
                  defaultValue={category.category_values?.find((v) => v.language_id === lang.id)?.value}
                  disabled={disabled}
                />
              </DisabledTooltip>
            )
          })}
        </div>
        {category.type !== 'warning-slates' && (
          <div>
            <DisabledTooltip reason={externalNameReason} className='block w-full'>
              <TextField
                label={'External Unique Name *'}
                value={currentCategory.external_unique_name ?? ''}
                error={externalUniqueNameError ?? undefined}
                disabled={!canEditExternalName}
                description={
                  'A stable identifier used to match this category across cycles and systems. ' +
                  'It must be unique per airline — if the same name is used for different content types (e.g. Movies, TV Series), append the content type to distinguish them. ' +
                  'Only admin users can edit this field after a snapshot has been successfully generated for this cycle.'
                }
                onChange={(e) => {
                  setCurrentCategory({ ...currentCategory, external_unique_name: e.target.value })
                  if (externalUniqueNameError) setExternalUniqueNameError(null)
                }}
                onBlur={() => {
                  if (!canEditExternalName) return
                  if (!currentCategory.external_unique_name) {
                    setExternalUniqueNameError('An external unique name is required.')
                    return
                  }
                  update({ external_unique_name: currentCategory.external_unique_name }, false, {
                    onError: (err) => {
                      const errors = err.response?.data?.errors
                      if (errors?.external_unique_name) {
                        setExternalUniqueNameError(
                          'This name is already in use by another category for this airline. ' +
                            'Try appending the content type (e.g. "Movies (TV Series)") to make it unique.'
                        )
                      }
                    },
                    onSuccess: () => setExternalUniqueNameError(null),
                  })
                }}
              />
            </DisabledTooltip>
          </div>
        )}

        <Combobox
          label={category.type !== 'warning-slates' ? 'Asset Types' : 'Asset Type'}
          placeholder='Movies, series, audiobooks, etc.'
          options={pluralAssetTypes?.filter((opt) => assetTypesOptionsFilter(opt.value)) ?? []}
          multiple={category.type !== 'warning-slates'}
          value={
            category.type !== 'warning-slates'
              ? (currentCategory?.asset_types ?? [])
              : (currentCategory?.asset_types?.[0] ?? null)
          }
          onValueChange={(selected) => {
            const nextAssetTypes = [selected].flat().filter(Boolean)
            update({ asset_types: nextAssetTypes })
            setCurrentCategory({ ...currentCategory, asset_types: nextAssetTypes })
          }}
          disabled={disabled}
        />
        {!currentCategory.is_root && !isAdRelated && (
          <Checkbox
            text='Enforce alphabetic sorting'
            checked={currentCategory.enforce_az_order || false}
            onClick={() => {
              update({ enforce_az_order: !currentCategory.enforce_az_order })
              setCurrentCategory({ ...currentCategory, enforce_az_order: !currentCategory.enforce_az_order })
            }}
          />
        )}

        {!category.is_root && category.type !== 'warning-slates' && (
          <>
            <DatePicker
              fullWidth
              label={'Start Date'}
              value={toUtcDateValue(category.start_date)}
              onValueChange={(value) => update({ start_date: fromUtcDateValue(value) }, false)}
              disabled={disabled}
            />
            <DatePicker
              fullWidth
              label={'End Date'}
              value={toUtcDateValue(category.end_date)}
              onValueChange={(value) => update({ end_date: fromUtcDateValue(value) }, false)}
              disabled={disabled}
            />
          </>
        )}
      </SettingsCard>
      {canEditCategory ? (
        <SettingsCard title='Danger zone' description='Permanent actions that cannot be undone.'>
          <div className='flex items-center justify-between gap-6'>
            <div className='min-w-0 flex-1'>
              <Text variant='sm-medium'>Delete this {noun}</Text>
              <Text as='p' variant='sm' tone='secondary' className='mt-1'>
                Permanently delete this {noun}. This can’t be undone.
              </Text>
            </div>
            <Button variant='destructive' onClick={deleteCategory}>
              Delete
            </Button>
          </div>
        </SettingsCard>
      ) : null}
    </div>
  )
}

export default Details
